/**
 * Simple Router for SPA with tab-based navigation
 */
export class Router {
  constructor(options = {}) {
    this.options = {
      mode: 'history', // 'history' for clean URLs, 'hash' for compatibility
      ...options
    };
    
    this.routes = new Map();
    this.currentRoute = null;
    this.isInitialized = false;
    this.tabsComponent = null;
    this.isProcessingRoute = false; // Flag to prevent circular navigation
    
    // Default routes
    this.defaultRoutes = {
      welcome: {
        title: 'Welcome',
        load: () => this.openTab('welcome', 'Welcome')
      },
      notFound: {
        title: '404 - Not Found',
        load: () => this.handle404()
      }
    };
    
    // Bind methods
    this.handleRouteChange = this.handleRouteChange.bind(this);
    this.navigate = this.navigate.bind(this);
    this.syncUrlToTab = this.syncUrlToTab.bind(this);
  }
  
  /**
   * Initialize the router
   * @param {object} tabsComponent - The tabs component instance
   */
  init(tabsComponent) {
    if (this.isInitialized) return this;
    
    this.tabsComponent = tabsComponent;
    
    // Capture initial path before setting up event listeners
    const initialPath = this.getCurrentPath();
    
    // Add event listeners based on routing mode
    if (this.options.mode === 'hash') {
      window.addEventListener('hashchange', this.handleRouteChange);
      
      // Initialize with default route if no hash provided
      if (!window.location.hash) {
        window.location.hash = '#/welcome';
      } else {
        // Process the current hash immediately with a small delay
        // to ensure components are ready
        setTimeout(() => this.loadRoute(initialPath), 0);
      }
    } else {
      // History API mode
      window.addEventListener('popstate', this.handleRouteChange);
      
      // Intercept all relative link clicks
      document.addEventListener('click', (e) => {
        // Find closest anchor element
        const anchor = e.target.closest('a');
        if (anchor && !anchor.getAttribute('target') && 
            anchor.hostname === window.location.hostname) {
          e.preventDefault();
          const path = anchor.pathname;
          this.navigate(path);
        }
      });
      
      // Initialize with default route if on root path
      if (initialPath === '/' || initialPath === '') {
        this.navigate('/welcome');
      } else {
        // Process the initial route with a small delay
        setTimeout(() => this.loadRoute(initialPath), 0);
      }
    }
    
    // Listen for tab changes to update URL
    document.addEventListener('tab:activate', (event) => {
      const tabId = event.detail.tabId;
      this.syncUrlToTab(tabId);
    });
    
    this.isInitialized = true;
    return this;
  }
  
  /**
   * Clean up event listeners
   */
  destroy() {
    if (this.options.mode === 'hash') {
      window.removeEventListener('hashchange', this.handleRouteChange);
    } else {
      window.removeEventListener('popstate', this.handleRouteChange);
    }
    
    document.removeEventListener('tab:activate', this.syncUrlToTab);
    
    this.isInitialized = false;
    return this;
  }
  
  /**
   * Add routes
   * @param {Object} routes - Route definitions
   */
  addRoutes(routes) {
    Object.entries(routes).forEach(([path, handler]) => {
      this.routes.set(this.normalizePath(path), handler);
    });
    
    return this;
  }
  
  /**
   * Handle route change
   */
  handleRouteChange() {
    const path = this.getCurrentPath();
    this.loadRoute(path);
  }
  
  /**
   * Get current path from URL
   */
  getCurrentPath() {
    if (this.options.mode === 'hash') {
      return window.location.hash.slice(1) || '/';
    } else {
      return window.location.pathname || '/';
    }
  }
  
  /**
   * Normalize path
   */
  normalizePath(path) {
    // Ensure path starts with /
    return path.startsWith('/') ? path : `/${path}`;
  }
  
  /**
   * Load a route
   * @param {string} path - Route path
   */
  loadRoute(path) {
    // Prevent circular navigation
    if (this.isProcessingRoute) {
      console.log('Route processing already in progress, skipping:', path);
      return;
    }
    
    this.isProcessingRoute = true;
    
    try {
    // Old file names still resolve, so previously shared links keep working
    const normalizedPath = this.normalizePath(path)
      .replace('CREATIONS.apps', 'creations.app')
      .replace('THOUGHTS.json', 'frameworks.json');
    console.log('Loading route:', normalizedPath);
    
    // Check for a file path route
    if (normalizedPath.startsWith('/file/')) {
      // Extract file path from the URL - take everything after /file/
      const filePath = normalizedPath.substring(6); // Remove '/file/'
      console.log('File path route detected:', filePath);
      
      // Special handling for ologies aggregation page
      if (filePath === 'ologies' || filePath === 'ologies/' || filePath === 'ologies/frameworks.json') {
        console.log('Opening ologies aggregation page in tab');
        // Open the ologies aggregation page in a tab instead of redirecting
        this.openTab('ologies/frameworks.json', 'frameworks.json');
        return;
      }
      
      // Open the file tab for other files
      this.openTab(filePath, filePath.split('/').pop());
      return;
    }
    
    // Check for exact route match
    if (this.routes.has(normalizedPath)) {
      console.log('Exact route match:', normalizedPath);
      const route = this.routes.get(normalizedPath);
      this.currentRoute = normalizedPath;
      
      // Execute route handler
      if (typeof route.load === 'function') {
        route.load(route);
      } else if (typeof route === 'function') {
        route();
      }
      
      // Update document title
      if (route.title) {
        document.title = `${route.title} | Our Appologies`;
      }
      
      return;
    }
    
    // Check for welcome route
    if (normalizedPath === '/' || normalizedPath === '/welcome') {
      console.log('Welcome route detected');
      this.defaultRoutes.welcome.load();
      return;
    }
    
      // Check for clean ology URLs (e.g., /ologies/joshifesto-v1)
      // Exclude XML files like sitemap-index.xml, sitemap-0.xml, etc.
    const ologyMatch = normalizedPath.match(/^\/ologies\/([^\/]+)\/?$/);
    if (ologyMatch) {
      const slug = ologyMatch[1];
        
        // Skip XML files (sitemaps, etc.) - let them be handled by the server
        if (slug.endsWith('.xml')) {
          console.log('XML file detected, skipping SPA routing:', slug);
          // Let the browser handle this as a regular navigation
          window.location.href = normalizedPath;
          return;
        }
        
      console.log('Clean ology URL detected:', slug);
      // Open the ology as a file tab (preserves SPA behavior)
      this.openTab(`ologies/${slug}`, slug);
      return;
    }
    
    // Check if this is a direct path to a file without the /file/ prefix
    // This improves handling direct links like example.com/about/the-team.md
    if (normalizedPath.includes('/') && !normalizedPath.startsWith('/api/')) {
      // Try to interpret as a file path (skip the leading slash)
      const possibleFilePath = normalizedPath.substring(1);
      
      // Special handling for ologies aggregation page (direct access)
      if (possibleFilePath === 'ologies/frameworks.json' || possibleFilePath === 'ologies') {
        console.log('Direct access to ologies aggregation page');
        this.openTab('ologies/frameworks.json', 'frameworks.json');
        return;
      }
      
      // Check if this matches known file patterns
      if (possibleFilePath.includes('.') || 
          possibleFilePath.startsWith('about/') || 
          possibleFilePath.startsWith('apps/') || 
          possibleFilePath.startsWith('ologies/') ||
          possibleFilePath.startsWith('were_not_sorry/')) {
        
          // Skip XML files in ologies directory - let them be handled by the server
          if (possibleFilePath.startsWith('ologies/') && possibleFilePath.endsWith('.xml')) {
            console.log('XML file in ologies detected, skipping SPA routing:', possibleFilePath);
            window.location.href = normalizedPath;
            return;
          }
          
        console.log('Attempting to handle as direct file path:', possibleFilePath);
        this.openTab(possibleFilePath, possibleFilePath.split('/').pop());
        return;
      }
    }
    
    // Handle 404
    console.log('Route not found, showing 404');
    this.defaultRoutes.notFound.load();
    } finally {
      // Always reset the flag, even if an error occurs
      this.isProcessingRoute = false;
    }
  }
  
  /**
   * Navigate to a route
   * @param {string} path - Route path
   * @param {Object} options - Navigation options
   */
  navigate(path, options = {}) {
    const normalizedPath = this.normalizePath(path);
    
    if (this.options.mode === 'hash') {
      window.location.hash = normalizedPath;
    } else {
      window.history.pushState(options.state || null, '', normalizedPath);
      this.handleRouteChange();
    }
    
    return this;
  }
  
  /**
   * Navigate to a file
   * @param {string} filePath - File path
   */
  navigateToFile(filePath) {
    console.log('Navigating to file:', filePath);
    
    // Special handling for ologies - use clean URLs for better social sharing
    // But exclude XML files (sitemaps, etc.)
    if (filePath.startsWith('ologies/') && 
        filePath !== 'ologies/frameworks.json' && 
        !filePath.endsWith('.xml')) {
      const slug = filePath.replace('ologies/', '');
      return this.navigate(`/ologies/${slug}`);
    }
    
    // Use /file/ prefix for all other files
    return this.navigate(`/file/${filePath}`);
  }
  
  /**
   * Sync URL to current tab
   * @param {string} tabId - Active tab ID
   */
  syncUrlToTab(tabId) {
    // Skip if this is already the current route
    if (this.currentRoute === tabId) return;
    
    console.log('Syncing URL to tab:', tabId);
    
    // Welcome tab has a special route
    if (tabId === 'welcome') {
      this.navigate('/welcome', { silent: true });
      return;
    }
    
    // Other tabs are treated as file paths
    if (tabId.includes('/')) {
      this.navigateToFile(tabId);
    }
  }
  
  /**
   * Open a tab
   * @param {string} tabId - Tab ID
   * @param {string} title - Tab title
   */
  openTab(tabId, title) {
    if (!this.tabsComponent) return;
    console.log('Opening tab:', tabId);
    
    // If tab exists, just activate it
    if (this.tabsComponent.hasTab(tabId)) {
      this.tabsComponent.activateTab(tabId);
      return;
    }
    
    // Always fire file:open event, but add a flag to prevent URL updates during route processing
    const openEvent = new CustomEvent('file:open', { 
      detail: { 
        filepath: tabId,
        title: title,
        fromRouter: this.isProcessingRoute // Flag to indicate this came from router
      },
      bubbles: true
    });
    document.dispatchEvent(openEvent);
  }
  
  /**
   * Handle 404 - Not Found
   */
  handle404() {
    if (!this.tabsComponent) return;
    
    const notFoundId = '404-not-found';
    const content = `
      <div class="not-found">
        <h2>404 - Page Not Found</h2>
        <p>The requested resource could not be found.</p>
        <button class="button" onclick="window.router.navigate('/welcome')">Go to Welcome</button>
      </div>
    `;
    
    // Create tab if doesn't exist
    if (!this.tabsComponent.hasTab(notFoundId)) {
      this.tabsComponent.addTab(notFoundId, '404 - Not Found', content, { active: true });
    } else {
      this.tabsComponent.activateTab(notFoundId);
    }
    
    document.title = '404 - Not Found | Our Appologies';
  }
}

// Create singleton instance
const router = new Router();

// Export singleton and constructor
export default router; 